import { itx, noItx } from "../../../src/translationItx/itx";
import { comparePdfLabels, pdfHeaderContext, pdfHeaderText, pdfUsesTags } from "../../../src/pdf/pdfTagName";
import { isTechnicalReportId } from "../../../src/pdf/technicalReports/catalog";
import { collectFuseReport } from "../../../src/pdf/fuseReport";
import { createFusePdf } from "../../../src/pdf/createFusePdf";
import { generateTags } from "../estada/C1_generateTags";
import { C1_renderFloorImage } from "../estada/C1_renderFloorImage";
import { downloadC1FloorPlanPdf, type C1_FloorPlanPdfPage } from "../plan/C1_FloorPlanPdf";
import {
  createC1RoomPlanNeighborContext,
  downloadC1RoomPlanPdf,
  type C1_RoomPlanPdfDiagram,
  type C1_RoomPlanPdfGroup,
} from "../plan/C1_RoomPlanPdf";
import { serializeC1PlanSvg } from "../plan/C1_PlanSvg";
import { downloadCompleteC1RaumbuchPdf } from "../raumbuch/C1_Raumbuch";
import { ensureEloEquipmentDefaults } from "../model/C1_ensureEloEquipmentDefaults";
import c1_state from "../model/C1_State";
import type { PaletteTool } from "../types/C1_AppTypes";
import { C1_FloorFinish, RoomType, type RoomTemplate } from "../types/C1_ModelTypes";
import { getPreferredRoomTemplates } from "../data/C1_roomTemplates";
import { existingRoomDraft } from "../model/C1_existingRoomDraft";
import type Room from "../../../src/models/Room";
import type { C1_MasterPdfChapterId, C1_MasterPdfConfig, C1_PdfPlanMode } from "../editor/C1_MasterPdfDialog";
import { createC1MasterPdf } from "../plan/C1_MasterPdf";
import { readPdfExportConfig, savePdfExportConfig } from "../../../src/pdf/PdfExportSettings";
import { pdfChapterTitle, selectPdfChapter, availablePdfConfig, type PdfApplicationMode } from "../../../src/pdf/PdfExportConfig";
import { collectGroupAddressReport } from "../../../src/pdf/groupAddressReport";
import { createGroupAddressPdf } from "../../../src/pdf/createGroupAddressPdf";
import type { C1_PdfChapterArtifact } from "../lib/C1_PdfChapter";
import {
  getCurrentToastToken,
  hideToast,
  showToast,
  updateToastMessage,
  updateToastProgress,
} from "../../../src/components/Toasts";
import { C1_EventKey } from "../C1_EventKey";
import { saveProject } from "../../../src/components/SaveProject";
import { askToSaveProjectBeforeLeaving } from "../../../src/components/dialogs/SaveProjectBeforeLeaving";
import type { C1_Floor } from "../model/C1_Floor";

type C1_TaggedObject = {
  owner?: { tag: string; name: string };
};

/** Provides the current ESTADA tag and name for sorting and PDF captions. */
function getTaggedLabel(object: C1_TaggedObject): { tag?: string; name?: string } {
  return object.owner ?? {};
}

/** Selects a floor through the current project-backed C1 state structure. */
function selectFloorForExport(floorId: string): C1_Floor | undefined {
  const floor = c1_state.project.getFloor(floorId);
  if (!floor) return undefined;

  c1_state._currentFloorOwner = floor.owner;
  c1_state.currentFloorId.value = floor.id;
  return floor;
}

export type C1_MenubarActionDependencies = {
  getPlanElement: () => HTMLElement | null;
  showInitialRoomPlacementPreview: (width: number, height: number, clientX: number, clientY: number) => void;
  beginPalettePointerPlacement: (pointerId: number, clientX: number, clientY: number) => void;
  openRoombook: () => void;
  closeRoombook: () => void;
  isRoombookOpen: () => boolean;
};

type C1_GeneratedPdfChapter = {
  id: C1_MasterPdfChapterId;
  title: string;
  artifact: C1_PdfChapterArtifact;
};

/** Waits for React to commit a view change; the timer also covers paused background tabs. */
function waitForRenderedFrames() {
  return new Promise<void>((resolve) => {
    const timer = window.setTimeout(resolve, 100);
    requestAnimationFrame(() => requestAnimationFrame(() => {
      window.clearTimeout(timer);
      resolve();
    }));
  });
}

export class C1_MenubarActions {
  constructor(private readonly dependencies: C1_MenubarActionDependencies) {}

  /** Plan exports read the rendered plan SVG, which is unmounted while the room book is open. */
  private async showPlanView(): Promise<() => void> {
    if (!this.dependencies.isRoombookOpen()) return () => {};
    this.dependencies.closeRoombook();
    await waitForRenderedFrames();
    return () => this.dependencies.openRoombook();
  }

  executeProjects() {
    askToSaveProjectBeforeLeaving("/");
  }

  executeSave() {
    void saveProject();
  }
  executeSettings() {
    state.dockManager.openSettings();
  }
  executeUndo() {
    c1_state.undo();
  }
  executeRedo() {
    c1_state.redo();
  }
  executeDelete() {
    c1_state.deleteSelection();
  }
  executeBuildingMode() {
    state.dockManager.selectMode("building");
    c1_state.eventLogger.addEvent(C1_EventKey.ModeSelected, { mode: "building" });
  }
  executeFurnishingMode() {
    state.dockManager.selectMode("furnishing");
    c1_state.eventLogger.addEvent(C1_EventKey.ModeSelected, { mode: "furnishing" });
  }
  executeElectricalMode() {
    state.dockManager.selectMode("EloConcept");
    c1_state.eventLogger.addEvent(C1_EventKey.ModeSelected, { mode: "EloConcept" });
  }
  executeViewFloorPlan() {
    generateTags(c1_state.project);
    state.undoManager.userInteractionEnded();
    this.dependencies.closeRoombook();
    c1_state.eventLogger.addEvent(C1_EventKey.ViewSelected, { view: "floorPlan" });
  }

  executeToolSelect(tool: PaletteTool, keepActive = false) {
    c1_state.cancelPastePlacement?.();
    c1_state.setSelection(undefined);
    c1_state.paletteFurnitureId.value = null;
    c1_state.dropPreview.value = null;
    if (tool === "room") {
      c1_state.paletteDragTool.value = null;
      c1_state.resetRoomDraft();
      const language = c1_state.language;
      const floor = c1_state.project.getFloor(c1_state.currentFloorId.value);
      const roomNames = new Set(floor?.rooms.map((room) => (room.owner?.name ?? "")) ?? []);
      const suggestion = floor
        ? getPreferredRoomTemplates(floor.level).find((template) => !roomNames.has(template.description[language]))
        : undefined;
      if (suggestion) {
        c1_state.roomDraft.value = {
          name: suggestion.description[language],
          type: suggestion.type,
          width: suggestion.defaultWidth ?? c1_state.roomDraft.value.width,
          height: suggestion.defaultHeight ?? c1_state.roomDraft.value.height,
          floorFinish: suggestion.defaultFloorFinish,
        };
      }
      state.dockManager.openPalette();
      return;
    }
    c1_state.paletteDragTool.value = !keepActive && c1_state.paletteDragTool.value === tool ? null : tool;
  }

  executeRoomTemplateSelect(
    template?: RoomTemplate,
    clientPosition?: { clientX: number; clientY: number },
    keepActive = false,
  ) {
    const nextDraft = template
      ? {
          name: template.description[c1_state.language],
          type: template.type,
          width: template.defaultWidth ?? c1_state.roomDraft.value.width,
          height: template.defaultHeight ?? c1_state.roomDraft.value.height,
          floorFinish: template.defaultFloorFinish,
        }
      : { name: "", type: RoomType.Office, width: 3, height: 2.5, floorFinish: C1_FloorFinish.Parquet1 };

    const alreadyActive =
      c1_state.paletteDragTool.value === "room" &&
      !c1_state.roomDraft.value.existingRoomId &&
      c1_state.roomDraft.value.name === nextDraft.name &&
      c1_state.roomDraft.value.type === nextDraft.type;

    c1_state.paletteFurnitureId.value = null;
    c1_state.dropPreview.value = null;
    if (alreadyActive && !keepActive) {
      c1_state.paletteDragTool.value = null;
      return;
    }

    c1_state.roomDraft.value = nextDraft;
    c1_state.paletteDragTool.value = "room";
    if (clientPosition) {
      this.dependencies.showInitialRoomPlacementPreview(
        nextDraft.width,
        nextDraft.height,
        clientPosition.clientX,
        clientPosition.clientY,
      );
    }
  }

  executeExistingRoomSelect(room: Room, clientPosition?: {clientX: number; clientY: number}, keepActive = false) {
    c1_state.cancelPastePlacement?.();
    const alreadyActive = c1_state.paletteDragTool.value === "room" && c1_state.roomDraft.value.existingRoomId === room.id;
    c1_state.setSelection(undefined);
    c1_state.paletteFurnitureId.value = null;
    c1_state.dropPreview.value = null;
    if (alreadyActive && !keepActive) {
      c1_state.paletteDragTool.value = null;
      return;
    }
    c1_state.roomDraft.value = existingRoomDraft(room);
    c1_state.paletteDragTool.value = "room";
    if (clientPosition) this.dependencies.showInitialRoomPlacementPreview(
      c1_state.roomDraft.value.width, c1_state.roomDraft.value.height, clientPosition.clientX, clientPosition.clientY,
    );
  }

  executeFurnitureSelect(furnitureId: string, keepActive = false) {
    c1_state.cancelPastePlacement?.();
    const alreadyActive = !c1_state.paletteExistingDeviceId.value && c1_state.paletteFurnitureId.value === furnitureId;
    c1_state.paletteExistingDeviceId.value = null;
    c1_state.setSelection(undefined);
    c1_state.paletteDragTool.value = null;
    c1_state.dropPreview.value = null;
    c1_state.paletteFurnitureId.value =
      !keepActive && alreadyActive ? null : furnitureId;
  }

  beginPalettePointerPlacement(pointerId: number, clientX: number, clientY: number) {
    this.dependencies.beginPalettePointerPlacement(pointerId, clientX, clientY);
  }

  async executeExportImage() {
    c1_state.floorImageRenderOptions.value = {
      includeAxes: false,
      includeDimensions: false,
    };
    try {
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
      });
      const planElement = this.dependencies.getPlanElement();
      if (!planElement) return;
      try {
        const dataUrl = await C1_renderFloorImage(planElement);
        const link = document.createElement("a");
        link.download = "grundriss.jpg";
        link.href = dataUrl;
        link.click();
      } catch (error) {
        console.error(noItx("Export failed:"), error);
      }
    } finally {
      c1_state.floorImageRenderOptions.value = null;
    }
  }

  async executeFloorPlanPdf(
    scope: "floor" | "all",
    download = true,
    presentationMode: C1_PdfPlanMode = readPdfExportConfig(state, "c1").chapters.find(chapter => chapter.id === "floorPlans")?.planMode ?? "design",
    projectName = c1_state.project.projectName,
    includeDimensions = readPdfExportConfig(state, "c1").chapters.find(chapter => chapter.id === "floorPlans")?.includeDimensions ?? true,
    eloItemScale = readPdfExportConfig(state, "c1").chapters.find(chapter => chapter.id === "floorPlans")?.eloItemScale ?? 1,
  ): Promise<C1_PdfChapterArtifact | undefined> {
    const restorePlanView = await this.showPlanView();
    const originalFloorId = c1_state.currentFloorId.value;
    const originalEditMode = c1_state.editMode.value;
    const originalSelection = c1_state.selection;
    const floors =
      scope === "floor"
        ? c1_state.project.floors.filter((floor) => floor.id === originalFloorId)
        : [...c1_state.project.floors].sort((left, right) =>
            comparePdfLabels(getTaggedLabel(left), getTaggedLabel(right), c1_state.language),
          );

    c1_state.floorImageRenderOptions.value = {
      includeAxes: false,
      includeDimensions,
      includeRoomNames: true,
      displayMode: presentationMode,
      eloItemScale,
    };
    c1_state.editMode.value = "EloConcept";
    c1_state.setSelection(undefined);
    try {
      const pdfFloors: C1_FloorPlanPdfPage[] = [];
      for (const floor of floors) {
        selectFloorForExport(floor.id);
        await new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
        const planElement = this.dependencies.getPlanElement();
        const planSvg = planElement?.querySelector<SVGSVGElement>(".C1_planSvg");
        if (!planSvg) continue;
        pdfFloors.push({
          id: floor.id,
          label: pdfHeaderText(floor.owner ?? floor),
          roomRects: floor.rooms.map((room) => {
            const rect = room.rect();
            return {
              roomId: room.id,
              x: (rect.x1 - c1_state.view.x1) * c1_state.svgPx,
              y: (rect.y1 - c1_state.view.y1) * c1_state.svgPx,
              width: (rect.x2 - rect.x1) * c1_state.svgPx,
              height: (rect.y2 - rect.y1) * c1_state.svgPx,
            };
          }),
          ...serializeC1PlanSvg(planSvg),
        });
      }
      if (pdfFloors.length) {
        const result = await downloadC1FloorPlanPdf(
          {
            projectName,
            dateText: new Intl.DateTimeFormat(state.settings.uiLanguage).format(new Date()),
            floors: pdfFloors,
          },
          download,
          !download,
        );
        if (download)
          c1_state.eventLogger.addEvent(C1_EventKey.PdfGenerated, {
            chapters: ["floorPlans"],
            scope,
            floorCount: pdfFloors.length,
          });
        return result;
      }
    } catch (error) {
      console.error(noItx("Could not create floor plan PDF."), error);
      if (!download) throw new Error(`Etagen-Grundrisse: ${error instanceof Error ? error.message : String(error)}`, { cause: error });
      window.alert(itx("c1.ui.Das_Grundriss_PDF_konnte_nicht_erstellt_werden_value1", "Das Grundriss-PDF konnte nicht erstellt werden.\n{{value1}}", { "{{value1}}": String(error instanceof Error ? error.message : String(error)) }));
    } finally {
      selectFloorForExport(originalFloorId);
      c1_state.editMode.value = originalEditMode;
      c1_state.setSelection(originalSelection);
      c1_state.floorImageRenderOptions.value = null;
      restorePlanView();
    }
  }

  async executeRoomPlanPdf(
    download = true,
    presentationMode: C1_PdfPlanMode = readPdfExportConfig(state, "c1").chapters.find(chapter => chapter.id === "roomPlans")?.planMode ?? "design",
    onProgress?: (progress: number, message: string) => void,
    showTags = c1_state.project.masterPdfConfig?.chapters.find((chapter) => chapter.id === "roomPlans")?.showTags ??
      true,
    projectName = c1_state.project.projectName,
    eloItemScale = readPdfExportConfig(state, "c1").chapters.find(chapter => chapter.id === "roomPlans")?.eloItemScale ?? 1,
  ): Promise<C1_PdfChapterArtifact | undefined> {
    showTags = showTags && pdfUsesTags();
    const restorePlanView = await this.showPlanView();
    let toastToken: number | undefined;
    if (download && !onProgress) {
      await showToast({
        title: itx("c1.ui.PDF_Export", "PDF-Export"),
        text: "Raumgrundrisse werden vorbereitet …",
        progress: true,
        keepVisible: true,
      });
      toastToken = getCurrentToastToken();
    }
    const reportProgress =
      onProgress ??
      ((progress: number, message: string) => {
        updateToastMessage(message, toastToken);
        updateToastProgress({ progress, toastToken });
      });
    reportProgress(1, itx("c1.ui.Raumgrundrisse_werden_vorbereitet", "Raumgrundrisse werden vorbereitet …"));
    const originalFloorId = c1_state.currentFloorId.value;
    const originalEditMode = c1_state.editMode.value;
    const originalSelection = c1_state.selection;
    const language = c1_state.language;
    const floors = [...c1_state.project.floors].sort((left, right) =>
      comparePdfLabels(getTaggedLabel(left), getTaggedLabel(right), language, showTags),
    );

    try {
      c1_state.floorImageRenderOptions.value = {
        includeAxes: false,
        includeDimensions: false,
        includeRoomNames: false,
        roomPlanExport: true,
        displayMode: presentationMode,
        eloItemScale,
      };
      c1_state.editMode.value = "EloConcept";
      c1_state.setSelection(undefined);
      const diagrams: Array<C1_RoomPlanPdfDiagram & { groupName: string; floorGroupId: string }> = [];
      for (const [floorIndex, floor] of floors.entries()) {
        selectFloorForExport(floor.id);
        await new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
        const planElement = this.dependencies.getPlanElement();
        const planSvg = planElement?.querySelector<SVGSVGElement>(".C1_planSvg");
        if (!planSvg) continue;
        const serialized = serializeC1PlanSvg(planSvg);
        const view = c1_state.view;
        const px = c1_state.svgPx;
        const cropMargin = 1.25;
        const sortedRooms = [...floor.rooms].sort((left, right) =>
          comparePdfLabels(getTaggedLabel(left), getTaggedLabel(right), language, showTags),
        );

        const roomsByName = new Map<string, { name: string; rooms: typeof sortedRooms }>();
        sortedRooms.forEach((room) => {
          const name = (room.owner?.name ?? "").trim() || itx("c1.ui.Unbenannter_Raum", "Unbenannter Raum");
          const key = name.toLocaleLowerCase(language);
          const group = roomsByName.get(key);
          if (group) group.rooms.push(room);
          else roomsByName.set(key, { name, rooms: [room] });
        });

        for (const { name: roomName, rooms } of roomsByName.values()) {
          const roomIds = new Set(rooms.map((room) => room.id));
          const rects = rooms.map((room) => room.rect());
          const rect = {
            x1: Math.min(...rects.map((candidate) => candidate.x1)),
            y1: Math.min(...rects.map((candidate) => candidate.y1)),
            x2: Math.max(...rects.map((candidate) => candidate.x2)),
            y2: Math.max(...rects.map((candidate) => candidate.y2)),
          };
          const ownOpeningIds = rooms.flatMap((room) => room.openings.map((opening) => opening.id));
          const partnerDoorIds = floor.rooms.flatMap((candidate) =>
            candidate.openings
              .filter((opening) => opening.kind === "door" && roomIds.has(opening.partnerRoom?.id ?? ""))
              .map((opening) => opening.id),
          );
          const roomTags = rooms
            .map((room) => room.owner?.tag)
            .filter(Boolean)
            .join(", ");
          const neighborContext = createC1RoomPlanNeighborContext({
            floor,
            rooms,
            project: c1_state.project,
            view,
            px,
          });
          const diagramCropMargin = Math.max(cropMargin, ...neighborContext.map(neighbor => neighbor.label.distance / px + Math.max(0.25, 12 / px)));
          /* Previous inline implementation retained temporarily for comparison.
          const tolerance = 0.001;
          const contextDepth = 0.8;
          const labelDistance = 0.42;
          const toSvgX = (value: number) => (value - view.x1) * px;
          const toSvgY = (value: number) => (value - view.y1) * px;
          const neighborContext = floor.rooms
            .filter((candidate) => !roomIds.has(candidate.id))
            .flatMap((candidate) => {
              const candidateRect = candidate.rect();
              const contacts = rooms.flatMap((targetRoom) => {
                const targetRect = targetRoom.rect();
                const verticalStart = Math.max(targetRect.y1, candidateRect.y1);
                const verticalEnd = Math.min(targetRect.y2, candidateRect.y2);
                const horizontalStart = Math.max(targetRect.x1, candidateRect.x1);
                const horizontalEnd = Math.min(targetRect.x2, candidateRect.x2);
                if (verticalEnd > verticalStart + tolerance && Math.abs(candidateRect.x2 - targetRect.x1) <= tolerance)
                  return [
                    {
                      side: "left" as const,
                      start: verticalStart,
                      end: verticalEnd,
                      boundary: targetRect.x1,
                      targetRoom,
                    },
                  ];
                if (verticalEnd > verticalStart + tolerance && Math.abs(candidateRect.x1 - targetRect.x2) <= tolerance)
                  return [
                    {
                      side: "right" as const,
                      start: verticalStart,
                      end: verticalEnd,
                      boundary: targetRect.x2,
                      targetRoom,
                    },
                  ];
                if (
                  horizontalEnd > horizontalStart + tolerance &&
                  Math.abs(candidateRect.y2 - targetRect.y1) <= tolerance
                )
                  return [
                    {
                      side: "top" as const,
                      start: horizontalStart,
                      end: horizontalEnd,
                      boundary: targetRect.y1,
                      targetRoom,
                    },
                  ];
                if (
                  horizontalEnd > horizontalStart + tolerance &&
                  Math.abs(candidateRect.y1 - targetRect.y2) <= tolerance
                )
                  return [
                    {
                      side: "bottom" as const,
                      start: horizontalStart,
                      end: horizontalEnd,
                      boundary: targetRect.y2,
                      targetRoom,
                    },
                  ];
                return [];
              });
              if (!contacts.length) return [];
              const contact = contacts.sort((left, right) => right.end - right.start - (left.end - left.start))[0];
              const middle = (contact.start + contact.end) / 2;
              const horizontalNeighbor = contact.side === "left" || contact.side === "right";
              const direction = contact.side === "left" || contact.side === "top" ? -1 : 1;
              const availableDepth = horizontalNeighbor
                ? contact.side === "left"
                  ? contact.boundary - candidateRect.x1
                  : candidateRect.x2 - contact.boundary
                : contact.side === "top"
                  ? contact.boundary - candidateRect.y1
                  : candidateRect.y2 - contact.boundary;
              const depth = Math.min(contextDepth, Math.max(0, availableDepth));
              const contextWall = (side: WallSide, position: number) => {
                const specialRoomFallback = isSpecialExteriorRoomType(candidate.type);
                const sourceRoom = specialRoomFallback ? contact.targetRoom : candidate;
                const sourceSide = specialRoomFallback ? contact.side : side;
                const sourcePosition = specialRoomFallback ? middle : contact.boundary;
                if (sourceRoom.isWallHidden(sourceSide, c1_state.project, sourcePosition)) return undefined;
                const segment = sourceRoom
                  .wallSegments(sourceSide, c1_state.project)
                  .find((item) => sourcePosition >= item.start - tolerance && sourcePosition <= item.end + tolerance);
                if (!segment) return undefined;
                const thicknessModel = segment.exterior
                  ? c1_state.project.settings.wallThicknessExterior
                  : c1_state.project.settings.wallThicknessInterior;
                const thickness = thicknessModel * px;
                const originalFill = segment.exterior
                  ? c1_state.project.settings.wallColorExterior
                  : c1_state.project.settings.wallColorInterior;
                if (horizontalNeighbor) {
                  const end = contact.boundary + direction * depth;
                  return {
                    x: toSvgX(Math.min(contact.boundary, end)),
                    y: toSvgY(position) - thickness / 2,
                    width: depth * px,
                    height: thickness,
                    outline: originalFill,
                    cutSide: contact.side,
                  };
                }
                const end = contact.boundary + direction * depth;
                return {
                  x: toSvgX(position) - thickness / 2,
                  y: toSvgY(Math.min(contact.boundary, end)),
                  width: thickness,
                  height: depth * px,
                  outline: originalFill,
                  cutSide: contact.side,
                };
              };
              const walls = (
                horizontalNeighbor
                  ? [
                      Math.abs(contact.start - candidateRect.y1) <= tolerance
                        ? contextWall("top", candidateRect.y1)
                        : undefined,
                      Math.abs(contact.end - candidateRect.y2) <= tolerance
                        ? contextWall("bottom", candidateRect.y2)
                        : undefined,
                    ]
                  : [
                      Math.abs(contact.start - candidateRect.x1) <= tolerance
                        ? contextWall("left", candidateRect.x1)
                        : undefined,
                      Math.abs(contact.end - candidateRect.x2) <= tolerance
                        ? contextWall("right", candidateRect.x2)
                        : undefined,
                    ]
              ).filter((wall): wall is NonNullable<typeof wall> => wall !== undefined);
              return [
                {
                  id: candidate.id,
                  name: candidate.name,
                  label: horizontalNeighbor
                    ? {
                        x: toSvgX(contact.boundary),
                        y: toSvgY(middle),
                        side: contact.side,
                        distance: labelDistance * px,
                      }
                    : {
                        x: toSvgX(middle),
                        y: toSvgY(contact.boundary),
                        side: contact.side,
                        distance: labelDistance * px,
                      },
                  walls,
                },
              ];
            });
          */
          diagrams.push({
            id: rooms.map((room) => room.id).join("+"),
            groupName: roomName,
            floorGroupId: floor.id,
            floorLabel: showTags ? pdfHeaderText(floor.owner ?? floor) : (floor.owner?.name ?? ""),
            floorName: (floor.owner?.name ?? ""),
            roomLabel: rooms.length === 1
              ? pdfHeaderText(rooms[0].owner ?? rooms[0], { ...pdfHeaderContext(), useDinConvention: false })
              : pdfHeaderText({ tag: roomTags, name: roomName }),
            markup: serialized.markup,
            roomIds: Array.from(roomIds),
            openingIds: Array.from(new Set([...ownOpeningIds, ...partnerDoorIds])),
            neighborContext,
            crop: {
              x: (rect.x1 - diagramCropMargin - view.x1) * px,
              y: (rect.y1 - diagramCropMargin - view.y1) * px,
              width: (rect.x2 - rect.x1 + diagramCropMargin * 2) * px,
              height: (rect.y2 - rect.y1 + diagramCropMargin * 2) * px,
            },
          });
        }
        reportProgress(
          5 + Math.round(((floorIndex + 1) / Math.max(floors.length, 1)) * 20),
          itx("c1.ui.Raumdaten_werden_aufbereitet", "Raumdaten werden aufbereitet …"),
        );
      }

      const groupsByName = new Map<string, C1_RoomPlanPdfGroup>();
      diagrams.forEach(({ groupName, floorGroupId, ...diagram }) => {
        const key = `${floorGroupId}\u0000${groupName.toLocaleLowerCase(language)}`;
        const group = groupsByName.get(key);
        if (group) group.diagrams.push(diagram);
        else groupsByName.set(key, { name: groupName, floorLabel: diagram.floorLabel, diagrams: [diagram] });
      });
      const groups = Array.from(groupsByName.values());
      if (groups.length) {
        return await downloadC1RoomPlanPdf(
          {
            projectName,
            dateText: new Intl.DateTimeFormat(state.settings.uiLanguage).format(new Date()),
            groups,
            showTags,
          },
          download,
          !download,
          (progress, message) => reportProgress(25 + Math.round(progress * 0.75), message),
        );
      }
      reportProgress(100, itx("c1.ui.Keine_Raumgrundrisse_zum_Exportieren_gefunden", "Keine Raumgrundrisse zum Exportieren gefunden."));
    } catch (error) {
      console.error(noItx("Could not create room plan PDF."), error);
      if (toastToken !== undefined) await hideToast(toastToken);
      if (!download) throw new Error(`Raumgrundrisse: ${error instanceof Error ? error.message : String(error)}`, { cause: error });
      window.alert(itx("c1.ui.Das_Raumgrundriss_PDF_konnte_nicht_erstellt_werden_value1", "Das Raumgrundriss-PDF konnte nicht erstellt werden.\n{{value1}}", { "{{value1}}": String(error instanceof Error ? error.message : String(error)) }));
    } finally {
      selectFloorForExport(originalFloorId);
      c1_state.editMode.value = originalEditMode;
      c1_state.setSelection(originalSelection);
      c1_state.floorImageRenderOptions.value = null;
      restorePlanView();
    }
  }

  async executePdf(roombookOpen: boolean) {
    if (!roombookOpen) {
      await this.executeFloorPlanPdf("all");
      return;
    }
    try {
      await downloadCompleteC1RaumbuchPdf();
      c1_state.eventLogger.addEvent(C1_EventKey.PdfGenerated, { chapters: ["roombook"] });
    } catch (error) {
      console.error(noItx("Could not create room book PDF."), error);
      window.alert(itx("c1.ui.Das_Raumbuch_PDF_konnte_nicht_erstellt_werden", "Das Raumbuch-PDF konnte nicht erstellt werden."));
    }
  }

  executeMasterPdf() {
    state.dockManager.openMasterPdf();
  }

  async confirmMasterPdf(input: C1_MasterPdfConfig, mode: PdfApplicationMode = "c1", singleChapterId?: C1_MasterPdfChapterId) {
    const savedConfig = savePdfExportConfig(state, mode, input);
    const config = availablePdfConfig(selectPdfChapter(savedConfig, singleChapterId), state);
    state.undoManager.userInteractionEnded();
    if (!config.chapters.some((chapter) => chapter.enabled)) return;
    const projectName = state.projectMetadata.name;
    await showToast({
      title: itx("c1.ui.PDF_Export", "PDF-Export"),
      text: "Master-PDF wird vorbereitet …",
      progress: true,
      keepVisible: true,
    });
    const toastToken = getCurrentToastToken();
    let restorePlanView = () => {};
    try {
      restorePlanView = await this.showPlanView();
      const chapters: C1_GeneratedPdfChapter[] = [];
      const exportChapters = config.chapters.filter(
        (item) => item.enabled && item.id !== "buttonList" && item.id !== "billOfMaterials",
      );
      for (const [index, chapter] of exportChapters.entries()) {
        if (chapter.id === "buttonList" || chapter.id === "billOfMaterials") continue;
        const chapterTitle = pdfChapterTitle(chapter);
        updateToastMessage(itx("c1.ui.Erstellen_value1", "Erstellen: {{value1}}", { "{{value1}}": String(chapterTitle) }), toastToken);
        const artifact =
          state.projectMetadata.isC1Project === false && (chapter.id === "floorPlans" || chapter.id === "roomPlans")
            ? await (await import("../../../src/pdf/createClassicPlanPdf")).createClassicPlanPdf(state, chapter, projectName)
            : chapter.id === "terminals" && chapter.terminalMode !== "list"
            ? await (await import("../../../src/pdf/createTerminalPlanPdf")).createTerminalPlanPdf(state, projectName, chapter.terminalSpacing, chapter.terminalShowUnusedWires ?? false)
            : isTechnicalReportId(chapter.id)
            ? await (await import("../../../src/pdf/createTechnicalReportPdf")).createTechnicalReportPdf(state, chapter.id, chapter, projectName)
            : chapter.id === "cabinetLayout"
            ? await (await import("../../../src/pdf/createCabinetLayoutPdf")).createCabinetLayoutPdf(state, chapter, projectName)
            : chapter.id === "fuseList"
            ? await createFusePdf(
                collectFuseReport(state.cabinets.asArray(), chapter.fuseSort), projectName, chapter.fuseMode,
                config.chapters.filter((item) => item.enabled).findIndex((item) => item.id === chapter.id) + 1,
              )
            : chapter.id === "groupAddresses"
            ? await createGroupAddressPdf(
                collectGroupAddressReport(state.addresses, chapter.detailedGroupAddressUsage ?? false),
                projectName,
              )
            : chapter.id === "floorPlans"
              ? await this.executeFloorPlanPdf("all", false, chapter.planMode, projectName, chapter.includeDimensions ?? true, chapter.eloItemScale ?? 1)
              : chapter.id === "roomPlans"
                ? await this.executeRoomPlanPdf(
                    false,
                    chapter.planMode,
                    (progress, message) => {
                      const chapterStart = (index / Math.max(exportChapters.length, 1)) * 75;
                      const chapterShare = 75 / Math.max(exportChapters.length, 1);
                      updateToastMessage(message, toastToken);
                      updateToastProgress({
                        progress: Math.round(chapterStart + (progress / 100) * chapterShare),
                        toastToken,
                      });
                    },
                    chapter.showTags ?? true,
                    projectName,
                    chapter.eloItemScale ?? 1,
                  )
                : await downloadCompleteC1RaumbuchPdf(chapter.roombookMode, false, projectName);
        if (!artifact) throw new Error(itx("c1.ui.value1_Es_konnten_keine_Seiten_erzeugt_werden", "{{value1}}: Es konnten keine Seiten erzeugt werden.", { "{{value1}}": String(chapterTitle) }));
        chapters.push({
          id: chapter.id,
          title: chapterTitle,
          artifact,
        });
        updateToastProgress({
          progress: Math.round(((index + 1) / Math.max(exportChapters.length, 1)) * 75),
          toastToken,
        });
      }
      await createC1MasterPdf({
        singleChapterId,
        mode,
        projectName,
        config,
        chapters,
        onProgress: (progress, message) => {
          updateToastMessage(message, toastToken);
          updateToastProgress({ progress, toastToken });
        },
      });
      c1_state.eventLogger.addEvent(C1_EventKey.PdfGenerated, {
        chapters: config.chapters.filter((chapter) => chapter.enabled).map((chapter) => chapter.id),
      });
    } catch (error) {
      console.error(noItx("Could not create combined PDF."), error);
      await hideToast(toastToken);
      await showToast({ isError: true, title: itx("c1.ui.PDF_Export", "PDF-Export"), text: itx("c1.ui.Das_Master_PDF_konnte_nicht_erstellt_werden_value1", "Das Master-PDF konnte nicht erstellt werden. {{value1}}", { "{{value1}}": String(error instanceof Error ? error.message : String(error)) }) });
    } finally {
      restorePlanView();
    }
  }

  executeInteractionMode() {
    c1_state.editMode.value = "interactionConcept";
    c1_state.furnitureDialogOpen.value = false;
    c1_state.eventLogger.addEvent(C1_EventKey.ModeSelected, { mode: "interactionConcept" });
  }

  executeGenerateTags() {
    ensureEloEquipmentDefaults(c1_state.project);
    generateTags(c1_state.project);
    state.undoManager.userInteractionEnded();
  }

  executeViewRoombook() {
    ensureEloEquipmentDefaults(c1_state.project);
    generateTags(c1_state.project);
    state.undoManager.userInteractionEnded();
    this.dependencies.openRoombook();
    c1_state.eventLogger.addEvent(C1_EventKey.ViewSelected, { view: "roombook" });
  }
}
